import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanagelapi.stechomeyazilim.info";

const MezatYonetimi = () => {
  document.title = "Mezat Yönetimi | HarmanaGEL";

  const [mezatlar, setMezatlar] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [detailModal, setDetailModal] = useState(false);
  const [selectedMezat, setSelectedMezat] = useState(null);
  const [teklifler, setTeklifler] = useState([]);
  const [teklifLoading, setTeklifLoading] = useState(false);

  const fetchMezatlar = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${BASE_URL}/MEZATLAR?$top=500&$orderby=ID desc&$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress),KATEGORILER($select=AD),MEZAT_DURUM`
      );
      const result = await response.json();
      if (result && result.value) setMezatlar(result.value);
    } catch (err) {
      setError("Mezatlar yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMezatlar();
  }, []);

  const handleViewDetail = async (mezat) => {
    setSelectedMezat(mezat);
    setDetailModal(true);
    setTeklifLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}/MEZAT_TEKLIFLERI?$filter=MEZAT_ID eq ${mezat.ID}&$orderby=TEKLIF_TUTARI desc&$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress)`
      );
      const result = await response.json();
      if (result && result.value) setTeklifler(result.value);
    } catch (err) {
      setTeklifler([]);
    } finally {
      setTeklifLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu mezatı silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/MEZATLAR(${id})`, { method: "DELETE" });
      setSuccessMsg("Mezat silindi.");
      fetchMezatlar();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setError("Silme sırasında hata oluştu.");
    }
  };

  const getDurumBadge = (status) => {
    const renkler = {
      "aktif": "success",
      "beklemede": "warning",
      "bitti": "secondary",
      "iptal": "danger",
    };
    return (
      <Badge color={renkler[status?.toLowerCase()] || "secondary"}>
        {status || "Bilinmiyor"}
      </Badge>
    );
  };

  const columns = useMemo(() => [
    {
      header: "ID",
      accessorKey: "ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => <span className="fw-bold">#{cell.getValue()}</span>,
    },
    {
      header: "Başlık",
      accessorKey: "BASLIK",
      enableSorting: true,
      enableColumnFilter: true,
      cell: (cell) => (
        <div>
          <div className="fw-medium">{cell.getValue() || cell.row.original.AD || "-"}</div>
          <small className="text-muted">{cell.row.original.KATEGORILER?.AD || "Kategori Yok"}</small>
        </div>
      ),
    },
    {
      header: "Başlangıç Fiyatı",
      accessorKey: "BASLANGIC_FIYATI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const val = cell.getValue();
        return val ? <span className="fw-medium">{parseFloat(val).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺</span> : "-";
      },
    },
    {
      header: "Güncel Teklif",
      accessorKey: "EN_YUKSEK_TEKLIF",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const val = cell.getValue();
        return val ? (
          <span className="fw-bold text-success">
            {parseFloat(val).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
          </span>
        ) : <span className="text-muted">Teklif Yok</span>;
      },
    },
    {
      header: "Satıcı",
      accessorKey: "KULLANICILAR",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const u = cell.getValue();
        return u ? <span>{u.AD_SOYAD}</span> : "-";
      },
    },
    {
      header: "Bitiş Tarihi",
      accessorKey: "BITIS_TARIHI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const d = cell.getValue();
        if (!d) return "-";
        const date = new Date(d);
        const now = new Date();
        const isExpired = date < now;
        return (
          <span className={isExpired ? "text-danger" : "text-success"}>
            {date.toLocaleDateString("tr-TR")}
            {isExpired && <i className="bx bx-time-five ms-1" title="Süresi doldu" />}
          </span>
        );
      },
    },
    {
      header: "Durum",
      accessorKey: "DURUM",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => getDurumBadge(cell.getValue() || cell.row.original.AKTIF_DURUM),
    },
    {
      header: "İşlemler",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cellProps) => {
        const m = cellProps.row.original;
        return (
          <div className="d-flex gap-2">
            <Button color="info" size="sm" outline onClick={() => handleViewDetail(m)}>
              <i className="bx bx-detail" />
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleDelete(m.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  ], []);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Mezat Yönetimi" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="card-title mb-0">Mezatlar</h4>
                    <Button color="primary" size="sm" onClick={fetchMezatlar} disabled={isLoading}>
                      <i className="bx bx-refresh me-1" />
                      Yenile
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={mezatlar}
                      isGlobalFilter={true}
                      SearchPlaceholder="Mezat ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Mezat Detay Modal */}
      <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="xl" centered>
        <ModalHeader toggle={() => setDetailModal(false)}>
          Mezat #{selectedMezat?.ID} — {selectedMezat?.BASLIK || selectedMezat?.AD}
        </ModalHeader>
        <ModalBody>
          {selectedMezat && (
            <>
              <Row className="mb-4">
                <Col md={6}>
                  <table className="table table-sm table-bordered">
                    <tbody>
                      <tr><th>Satıcı</th><td>{selectedMezat.KULLANICILAR?.AD_SOYAD || "-"}</td></tr>
                      <tr><th>Kategori</th><td>{selectedMezat.KATEGORILER?.AD || "-"}</td></tr>
                      <tr>
                        <th>Başlangıç Fiyatı</th>
                        <td>{selectedMezat.BASLANGIC_FIYATI ? `${parseFloat(selectedMezat.BASLANGIC_FIYATI).toLocaleString("tr-TR")} ₺` : "-"}</td>
                      </tr>
                      <tr>
                        <th>En Yüksek Teklif</th>
                        <td className="fw-bold text-success">
                          {selectedMezat.EN_YUKSEK_TEKLIF ? `${parseFloat(selectedMezat.EN_YUKSEK_TEKLIF).toLocaleString("tr-TR")} ₺` : "Teklif yok"}
                        </td>
                      </tr>
                      <tr><th>Bitiş Tarihi</th><td>{selectedMezat.BITIS_TARIHI ? new Date(selectedMezat.BITIS_TARIHI).toLocaleString("tr-TR") : "-"}</td></tr>
                      <tr><th>Durum</th><td>{getDurumBadge(selectedMezat.DURUM || selectedMezat.AKTIF_DURUM)}</td></tr>
                    </tbody>
                  </table>
                </Col>
              </Row>

              <h6>Teklifler ({teklifler.length})</h6>
              {teklifLoading ? (
                <div className="text-center py-3"><Spinner color="primary" size="sm" /></div>
              ) : teklifler.length > 0 ? (
                <table className="table table-sm table-striped">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Kullanıcı</th>
                      <th>Teklif Tutarı</th>
                      <th>Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teklifler.map((t, i) => (
                      <tr key={i} className={i === 0 ? "table-success fw-bold" : ""}>
                        <td>{i + 1}</td>
                        <td>{t.KULLANICILAR?.AD_SOYAD || `ID: ${t.KULLANICI_ID}`}</td>
                        <td>{parseFloat(t.TEKLIF_TUTARI || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺</td>
                        <td>{t.TEKLIF_TARIHI ? new Date(t.TEKLIF_TARIHI).toLocaleString("tr-TR") : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-muted text-center py-3">Henüz teklif yapılmamış.</p>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setDetailModal(false)}>Kapat</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default MezatYonetimi;
